import { useState } from "react";
import {
  Sidebar,
  SidebarContent,
  SidebarProvider,
  SidebarTrigger,
} from "../components/ui/sidebar";
import { Button } from "../components/ui/button";

import { Badge } from "../components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import {
  Users,
  Clock,
  Package,
  BarChart3,
  Home,
  UserCheck,
  User,
  LogOut,
} from "lucide-react";
import { Dashboard } from "../components/Dashboard";
import { ClientManagement } from "../components/ClientManagement";
import { SubscriptionManagement } from "../components/SubscriptionManagement";
import { PackageManagement } from "../components/PackageManagement";
import { UsageTracking } from "../components/UsageTracking";
import { Reports } from "../components/Reports";
import { CheckIn } from "../components/CheckIn";
import { Login } from "../components/Login";
import { UserProfile } from "../components/UserProfile";
import { VersionHistory } from "../components/VersionHistory";
import { VersionDisplay } from "../components/VersionDisplay";
import { MemberDashboard } from "../components/MemberDashboard";

interface User {
  username: string;
  role: string;
  fullName: string;
  avatar: string;
}

export default function App() {
  const [activeView, setActiveView] = useState("dashboard");
  const [currentUser, setCurrentUser] = useState<User | null>(null);

  // Role-based navigation items
  const getNavigationItems = (role: string) => {
    if (role === "Member") {
      return [{ id: "dashboard", label: "Packages", icon: Package }];
    }

    if (role === "Space Manager") {
      return [
        {
          id: "checkin",
          label: "Tap In/Out",
          icon: UserCheck,
          prominent: true,
        },
        { id: "clients", label: "Client Management", icon: Users },
      ];
    }

    // System Admin gets full navigation
    if (role === "System Admin") {
      return [
        {
          id: "checkin",
          label: "Tap In/Out",
          icon: UserCheck,
          prominent: true,
        },
        { id: "dashboard", label: "Dashboard", icon: Home },
        { id: "clients", label: "Client Management", icon: Users },
        { id: "subscriptions", label: "Subscriptions", icon: Clock },
        { id: "packages", label: "Packages", icon: Package },
        { id: "reports", label: "Reports", icon: BarChart3 },
      ];
    }

    // Default fallback
    return [];
  };

  const navigationItems = currentUser
    ? getNavigationItems(currentUser.role)
    : [];

  const handleLogin = (userData: User) => {
    setCurrentUser(userData);
    // Set default view based on role
    if (userData.role === "Member") {
      setActiveView("dashboard"); // For members, dashboard shows packages
    } else {
      setActiveView("checkin"); // For staff, default to tap in/out
    }
  };

  const handleLogout = () => {
    setCurrentUser(null);
    setActiveView("dashboard");
  };

  const renderContent = () => {
    // Member-specific routing
    if (currentUser?.role === "Member") {
      switch (activeView) {
        case "dashboard":
          return <MemberDashboard currentUser={currentUser} />;
        case "profile":
          return (
            <UserProfile currentUser={currentUser} onLogout={handleLogout} />
          );
        case "version-history":
          return <VersionHistory />;
        default:
          return <MemberDashboard currentUser={currentUser} />;
      }
    }

    // Staff routing (System Admin and Space Manager)
    switch (activeView) {
      case "dashboard":
        return <Dashboard />;
      case "checkin":
        return <CheckIn />;
      case "clients":
        return <ClientManagement />;
      case "subscriptions":
        return <SubscriptionManagement />;
      case "packages":
        return <PackageManagement />;
      case "usage":
        return <UsageTracking />;
      case "reports":
        return <Reports />;
      case "profile":
        return currentUser ? (
          <UserProfile currentUser={currentUser} onLogout={handleLogout} />
        ) : (
          <Dashboard />
        );
      case "version-history":
        return <VersionHistory />;
      default:
        return <Dashboard />;
    }
  };

  const getPageTitle = () => {
    if (activeView === "profile") return "Profile & Settings";
    if (currentUser?.role === "Member" && activeView === "dashboard")
      return "Packages";
    return (
      navigationItems.find((item) => item.id === activeView)?.label ||
      "Dashboard"
    );
  };

  // Show login screen if not authenticated
  if (!currentUser) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full">
        <Sidebar variant="sidebar" side="left">
          <SidebarContent>
            <div className="flex flex-col h-full">
              <div className="flex-1 p-6">
                <h2 className="mb-6">Coworking Hub</h2>

                {/* User Info */}
                <div className="mb-6 p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Avatar className="h-10 w-10">
                      <AvatarImage
                        src={currentUser.avatar}
                        alt={currentUser.fullName}
                      />
                      <AvatarFallback>
                        {currentUser.fullName
                          .split(" ")
                          .map((n) => n[0])
                          .join("")}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">
                        {currentUser.fullName}
                      </p>
                      <div className="flex items-center gap-2">
                        <Badge
                          variant="secondary"
                          className={`text-xs ${
                            currentUser.role === "System Admin"
                              ? "bg-red-100 text-red-800"
                              : currentUser.role === "Space Manager"
                              ? "bg-blue-100 text-blue-800"
                              : currentUser.role === "Member"
                              ? "bg-green-100 text-green-800"
                              : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {currentUser.role}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </div>

                <nav className="space-y-2">
                  {navigationItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = activeView === item.id;

                    // Special styling for prominent buttons (like Tap In/Out for staff)
                    if (item.prominent) {
                      return (
                        <Button
                          key={item.id}
                          variant={isActive ? "default" : "outline"}
                          className={`w-full justify-start h-12 ${
                            isActive
                              ? "bg-blue-600 hover:bg-blue-700 text-white"
                              : "border-blue-200 hover:bg-blue-50 hover:border-blue-300"
                          }`}
                          onClick={() => setActiveView(item.id)}
                        >
                          <Icon className="mr-2 h-5 w-5" />
                          <span className="font-medium">{item.label}</span>
                        </Button>
                      );
                    }

                    return (
                      <Button
                        key={item.id}
                        variant={isActive ? "default" : "ghost"}
                        className="w-full justify-start"
                        onClick={() => setActiveView(item.id)}
                      >
                        <Icon className="mr-2 h-4 w-4" />
                        {item.label}
                      </Button>
                    );
                  })}
                </nav>

                {/* User Actions */}
                <div className="mt-6 pt-6 border-t border-sidebar-border space-y-2">
                  <Button
                    variant={activeView === "profile" ? "default" : "ghost"}
                    className="w-full justify-start"
                    onClick={() => setActiveView("profile")}
                  >
                    <User className="mr-2 h-4 w-4" />
                    Profile & Settings
                  </Button>
                  <Button
                    variant="ghost"
                    className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={handleLogout}
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign Out
                  </Button>
                </div>
              </div>

              {/* Version Display at Bottom */}
              <div className="p-6 pt-0 border-t border-sidebar-border">
                <VersionDisplay
                  onNavigate={() => setActiveView("version-history")}
                />
              </div>
            </div>
          </SidebarContent>
        </Sidebar>

        <main className="flex-1 overflow-auto">
          <div className="border-b p-4">
            <div className="flex items-center gap-4">
              <SidebarTrigger />
              <h1>{getPageTitle()}</h1>

              {/* User Badge in Header */}
              <div className="ml-auto">
                <Badge variant="outline" className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  {currentUser.fullName} ({currentUser.role})
                </Badge>
              </div>
            </div>
          </div>

          <div className="p-6">{renderContent()}</div>
        </main>
      </div>
    </SidebarProvider>
  );
}
